import { PAIRING_TOKEN_BYTES } from "./pairing";

/**
 * The typed pairing code: `K7M2-9QXD-4FHP`, for when the camera can't be used.
 *
 * It is two parts, and they are kept apart on purpose:
 *  - `id` (the first 4 characters) is a lookup key. The phone sends it to the relay, which answers with the address (public key)
 *    of the computer that has a pairing window open under that id. The relay never sees the rest.
 *  - `secret` (the last 8 characters) is the one-time token. It is only ever shown on the computer and typed on the phone, and
 *    travels to the computer inside the end-to-end encrypted pairing handshake, exactly like the long QR token. The computer
 *    stores only its hash.
 *
 * Numbers. The alphabet has 31 symbols (no 0 O 1 I L, which are read and typed wrong), so the secret is 31^8 = 8.5e11 values
 * (39.7 bits). Guessing is capped where it matters: the computer closes the pairing window after 5 wrong tries (see
 * PairingOffers), so a blind guess succeeds with probability about 5 / 8.5e11 = 6e-12 per window, the window lasts 5 minutes and
 * is single use, and the owner still has to press Allow. The id is 31^4 = 923,521 values: it only has to be unique among the
 * handful of windows open at once, and anyone who learns it learns only a public key, so it is rate limited at the relay but is
 * not a secret.
 */
export const SHORT_ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
export const SHORT_ID_CHARS = 4;
export const SHORT_SECRET_CHARS = 8;
const TOTAL = SHORT_ID_CHARS + SHORT_SECRET_CHARS;

export interface ShortCode {
  id: string;
  secret: string;
}

/** A fresh random code. `random` must be cryptographically strong (the default is Web Crypto, available in Node 22 and browsers). */
export function generateShortCode(random: (n: number) => Uint8Array = (n) => crypto.getRandomValues(new Uint8Array(n))): ShortCode {
  let out = "";
  const limit = 256 - (256 % SHORT_ALPHABET.length); // rejection sampling: every symbol equally likely
  while (out.length < TOTAL) {
    for (const b of random(TOTAL * 2)) {
      if (b < limit && out.length < TOTAL) out += SHORT_ALPHABET[b % SHORT_ALPHABET.length];
    }
  }
  return { id: out.slice(0, SHORT_ID_CHARS), secret: out.slice(SHORT_ID_CHARS) };
}

/** `K7M2-9QXD-4FHP`. */
export function formatShortCode(c: ShortCode): string {
  const all = c.id + c.secret;
  return `${all.slice(0, 4)}-${all.slice(4, 8)}-${all.slice(8, 12)}`;
}

/** What a person might type: any case, with or without dashes or spaces. Null when it is not a complete, valid code. */
export function parseShortCode(input: string): ShortCode | null {
  const s = input.toUpperCase().replace(/[\s-]/g, "");
  if (s.length !== TOTAL) return null;
  for (const ch of s) if (!SHORT_ALPHABET.includes(ch)) return null;
  return { id: s.slice(0, SHORT_ID_CHARS), secret: s.slice(SHORT_ID_CHARS) };
}

export const isShortId = (id: string): boolean => id.length === SHORT_ID_CHARS && [...id].every((ch) => SHORT_ALPHABET.includes(ch));

/**
 * Tidies what is being typed into the code field: uppercase, dashes after every 4 characters. Anything that is not
 * plausibly a short code (a long FM1 code, a link, a name) is returned unchanged.
 */
export function formatShortCodeInput(text: string): string {
  if (/^\s*(FM1\.|https?:|.*[#/.:=])/i.test(text)) return text;
  const s = text.toUpperCase().replace(/[\s-]/g, "");
  if (s.length === 0 || s.length > TOTAL || ![...s].every((ch) => SHORT_ALPHABET.includes(ch))) return text;
  return s.replace(/(.{4})(?=.)/g, "$1-");
}

/**
 * The 16-byte pairing token a short code stands for: the 8 secret characters, zero padded. The computer hashes this exactly
 * like a QR token, so the same `pair.request` message carries it.
 */
export function shortTokenFromSecret(secret: string): Uint8Array {
  const out = new Uint8Array(PAIRING_TOKEN_BYTES);
  for (let i = 0; i < secret.length && i < PAIRING_TOKEN_BYTES; i++) out[i] = secret.charCodeAt(i);
  return out;
}
